import Constants from "expo-constants";
import appConfig from "../app.config";
import type { Content as IntercomContent } from "./hooks/useIntercom";

const envVars = {
  API_URL: process.env.API_URL,
  V3_API_URL: process.env.V3_API_URL,
  APP_ENV: process.env.APP_ENV,
  ONE_LOGIN_CLIENT_ID: process.env.ONE_LOGIN_CLIENT_ID,
  ONE_LOGIN_ISSUER_URL: process.env.ONE_LOGIN_ISSUER_URL,
  RAYGUN_API_KEY: process.env.RAYGUN_API_KEY,
  WEB_AUTH_BASE_URL: process.env.WEB_AUTH_BASE_URL,
  WEB_DOMAIN: process.env.WEB_DOMAIN,
  GOOGLE_PLACES_API_KEY: process.env.GOOGLE_PLACES_API_KEY,
  GOOGLE_PLACES_API_KEY_UNRESTRICTED: process.env.GOOGLE_PLACES_API_KEY_UNRESTRICTED,
  GTM_ID: process.env.GTM_ID,
  GTM_AUTH: process.env.GTM_AUTH,
  GTM_PREVIEW: process.env.GTM_PREVIEW,
  CI_BUILD_NUMBER: process.env.CI_BUILD_NUMBER,
  ALGOLIA_APP_ID: process.env.ALGOLIA_APP_ID,
  ALGOLIA_API_KEY: process.env.ALGOLIA_API_KEY,
  ALGOLIA_JOBS_INDEX: process.env.ALGOLIA_JOBS_INDEX,
  GOOGLE_MAPS_API_URL: process.env.GOOGLE_MAPS_API_URL,
  GEOCODE_API_URL: process.env.GEOCODE_API_URL,
  FLAGR_API_URL: process.env.FLAGR_API_URL,
  DATADOG_APPLICATION_ID: process.env.DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN: process.env.DATADOG_CLIENT_TOKEN,
  AUTH0_DOMAIN: process.env.AUTH0_DOMAIN,
  AUTH0_CLIENT_ID: process.env.AUTH0_CLIENT_ID,
  AUTH0_AUDIENCE: process.env.AUTH0_AUDIENCE,
  AUTH_IMPL: process.env.AUTH_IMPL,
  STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
};

function getEnvVar(key: keyof typeof envVars, defaultValue?: any) {
  // try to get variable from system, then expo config and finally default value
  // this logic is to make sure it works for all types of build: web, eas update, eas build, local development
  return envVars[key] || (Constants.expoConfig?.extra || {})[key] || defaultValue;
}

interface Config {
  accountDomain: string;
  algoliaApiKey: string;
  algoliaAppId: string;
  algoliaJobsIndex: string;
  apiUrl: string;
  publicApiUrl: string;
  appScheme: string;
  appSlug: string;
  appVersion: string;
  auth0Audience: string;
  auth0ClientId: string;
  auth0Domain: string;
  authImpl: "auth0" | undefined;
  ciBuildNumber: string;
  geocodeApiUrl: string;
  googleMapsApiUrl: string;
  googlePlacesApiKey: string;
  googlePlacesApiKeyUnrestricted: string;
  gtmAuth: string;
  gtmId: string;
  gtmPreview: string;
  intercomContent: Record<
    | "deferredCompAutomaticActivityLearnMore"
    | "deferredCompHomeLearnMore"
    | "deferredCompHowItWorks"
    | "deferredCompPlanFundsInformation"
    | "deferredCompPlanTransition"
    | "howDoJobPreferencesWork"
    | "ideasAndFeedbackSurvey"
    | "jobsHowToUpload"
    | "loginTrouble"
    | "accountEditName",
    IntercomContent
  >;
  isProd: boolean;
  jobsDomain: string;
  oneLoginClientId: string;
  oneLoginIssuerUrl: string;
  raygunApiKey?: string;
  v3ApiUrl: string;
  webAuthBaseUrl: string;
  webDomain: string;
  flagrApiUrl: string;
  dataDogApplicationId: string;
  dataDogClientToken: string;
  stripePublishableKey: string;
}

const isProd = getEnvVar("APP_ENV", "dev") === "production";

const intercomContent = {
  deferredCompAutomaticActivityLearnMore: { article: "7967335" },
  deferredCompHomeLearnMore: { article: "7967342" },
  deferredCompHowItWorks: { article: "7967344" },
  deferredCompPlanTransition: { article: "8168153" },
  deferredCompPlanFundsInformation: { article: "8291222" },
  ideasAndFeedbackSurvey: { survey: "33964445" },
  loginTrouble: { article: "7967340" },
  howDoJobPreferencesWork: { article: "7967300" },
  jobsHowToUpload: { article: "8168155" },
  accountEditName: { article: "8887906" },
};

const apiUrl = getEnvVar("API_URL", "http://localhost:3000/membership/graphql");

const config: Config = {
  algoliaApiKey: getEnvVar("ALGOLIA_API_KEY", "856feed87ba76eea89ce57fb21008370"),
  algoliaAppId: getEnvVar("ALGOLIA_APP_ID", "0TSYHLZXNU"),
  algoliaJobsIndex: getEnvVar("ALGOLIA_JOBS_INDEX", "jw-test-jobs"),
  apiUrl,
  publicApiUrl: apiUrl.replace("/graphql", "/public-graphql"),
  appScheme: appConfig.scheme,
  appSlug: appConfig.slug,
  appVersion: appConfig.version,
  auth0Audience: getEnvVar("AUTH0_AUDIENCE", "dev-pga-membership"),
  auth0ClientId: getEnvVar("AUTH0_CLIENT_ID", "vbAXCvOIXCL5Zme4lvHRz6LuzqVnpYnj"),
  auth0Domain: getEnvVar("AUTH0_DOMAIN", "auth0.dev.pga.com"),
  authImpl: getEnvVar("AUTH_IMPL", "auth0"),
  ciBuildNumber: getEnvVar("CI_BUILD_NUMBER"),
  geocodeApiUrl: getEnvVar("GEOCODE_API_URL", "http://localhost:3000/api/geo"),
  googleMapsApiUrl: getEnvVar("GOOGLE_MAPS_API_URL", "https://maps.googleapis.com/maps/api"),
  googlePlacesApiKey: getEnvVar("GOOGLE_PLACES_API_KEY"),
  googlePlacesApiKeyUnrestricted: getEnvVar("GOOGLE_PLACES_API_KEY_UNRESTRICTED", "AIzaSyBnYhgzQIV8PtWwIQzuD0IWI-0qkJhOgWY"),
  gtmAuth: getEnvVar("GTM_AUTH"),
  gtmId: getEnvVar("GTM_ID"),
  gtmPreview: getEnvVar("GTM_PREVIEW"),
  intercomContent,
  isProd,
  jobsDomain: appConfig.extra.jobsDomain,
  accountDomain: appConfig.extra.accountDomain,
  oneLoginClientId: getEnvVar("ONE_LOGIN_CLIENT_ID", "60932f80-eb62-013b-8172-0a544c6f9ddb121302"),
  oneLoginIssuerUrl: getEnvVar("ONE_LOGIN_ISSUER_URL", "https://pgahq-dev.onelogin.com/oidc/2"),
  raygunApiKey: getEnvVar("RAYGUN_API_KEY"),
  v3ApiUrl: getEnvVar("V3_API_URL", "https://developers.sandboxpga.org/graphql"),
  webAuthBaseUrl: getEnvVar("WEB_AUTH_BASE_URL", "https://auth.sandboxpga.org/v1"),
  webDomain: Constants.expoConfig?.extra?.webDomain,
  flagrApiUrl: getEnvVar("FLAGR_API_URL", "https://flagr.dev.pga.com/api/v1"),
  dataDogApplicationId: getEnvVar("DATADOG_APPLICATION_ID"),
  dataDogClientToken: getEnvVar("DATADOG_CLIENT_TOKEN"),
  stripePublishableKey: getEnvVar("STRIPE_PUBLISHABLE_KEY", "pk_test_wIaocDJyRaoDKVl9wP4Xuore0080xDIrPG"),
};

export default config;
